// Membres de la famille de fantômes : nom, trait de caractère et portrait par langue, et
// comportement de dérive propre à chacun (pace = vitesse relative, wander = tendance à changer
// de cap). Le i-ème fantôme d'un niveau est le i-ème membre ; au-delà de la liste, on repart du
// début en donnant un rôle (« Izami père »).
// Ce sont les 15 kami de la planche « Nos amis les Kami » faite par Christophe NEY avec Nano Banana (portraits
// générés puis détourés, voir public/kami/). Le nom affiché est leur petit nom, dérivé du nom
// complet du kami d'origine (ex. Izanami → Izami), validé sans risque de copyright le 20 sept. 2026.
import { KAMI_STORIES } from './kamiStories.js';

export const FAMILY_MEMBERS = Object.freeze([
  { pace: 0.6, wander: 0.2, sprite: '/kami/izami.png', fr: { name: 'Izami', trait: 'Toujours polie, même trois siècles après sa mort.' }, en: { name: 'Izami', trait: 'Always polite, even three centuries after death.' } },
  { pace: 0.5, wander: 0.15, sprite: '/kami/nagi.png', fr: { name: 'Nagi', trait: 'Calme absolu, sauf quand on oublie sa branche.' }, en: { name: 'Nagi', trait: 'Absolute calm, except when you forget his branch.' } },
  { pace: 1.0, wander: 0.3, sprite: '/kami/tera.png', fr: { name: 'Tera', trait: 'Illumine la pièce, parfois un peu trop.' }, en: { name: 'Tera', trait: 'Lights up the room, sometimes a bit too much.' } },
  { pace: 1.1, wander: 0.3, sprite: '/kami/hachi.png', fr: { name: 'Hachi', trait: 'Vise juste, même les yeux fermés.' }, en: { name: 'Hachi', trait: 'Hits the mark, even with eyes closed.' } },
  { pace: 1.3, wander: 1.1, sprite: '/kami/koto.png', fr: { name: 'Koto', trait: 'Quatre avis différents sur tout, en même temps.' }, en: { name: 'Koto', trait: 'Four different opinions on everything, at once.' } },
  { pace: 0.5, wander: 0.2, sprite: '/kami/kane.png', fr: { name: 'Kane', trait: 'A un livre pour chaque situation, même les pique-niques.' }, en: { name: 'Kane', trait: 'Has a book for every situation, even picnics.' } },
  { pace: 0.4, wander: 0.1, sprite: '/kami/tsuki.png', fr: { name: 'Tsuki', trait: 'Dort le jour, brille la nuit.' }, en: { name: 'Tsuki', trait: 'Sleeps by day, shines by night.' } },
  { pace: 1.5, wander: 1.0, sprite: '/kami/susa.png', fr: { name: 'Susa', trait: 'Un peu bagarreur, beaucoup attachant.' }, en: { name: 'Susa', trait: 'A bit of a brawler, very endearing.' } },
  { pace: 0.9, wander: 0.4, sprite: '/kami/nari.png', fr: { name: 'Nari', trait: 'Partage son repas avec qui veut bien l’écouter.' }, en: { name: 'Nari', trait: 'Shares his meal with anyone who’ll listen.' } },
  { pace: 0.7, wander: 0.5, sprite: '/kami/saru.png', fr: { name: 'Saru', trait: 'Connaît tous les chemins, sauf le plus court.' }, en: { name: 'Saru', trait: 'Knows every path except the shortest one.' } },
  { pace: 1.4, wander: 1.2, sprite: '/kami/uzu.png', fr: { name: 'Uzu', trait: 'Danse d’abord, réfléchit après.' }, en: { name: 'Uzu', trait: 'Dances first, thinks later.' } },
  { pace: 0.6, wander: 0.3, sprite: '/kami/ujin.png', fr: { name: 'Ujin', trait: 'Prend son temps, même en pleine tempête.' }, en: { name: 'Ujin', trait: 'Takes his time, even in a storm.' } },
  { pace: 0.8, wander: 0.3, sprite: '/kami/kama.png', fr: { name: 'Kama', trait: 'Ne fait peur à personne, essaie pourtant très fort.' }, en: { name: 'Kama', trait: 'Scares no one, though he tries very hard.' } },
  { pace: 1.6, wander: 0.9, sprite: '/kami/rai.png', fr: { name: 'Rai', trait: 'Ne sait pas jouer doucement du tambour.' }, en: { name: 'Rai', trait: 'Doesn’t know how to drum quietly.' } },
  { pace: 1.2, wander: 0.8, sprite: '/kami/fuji.png', fr: { name: 'Fuji', trait: 'Arrive toujours en coup de vent.' }, en: { name: 'Fuji', trait: 'Always arrives in a gust of wind.' } },
]);

/** Le membre n°`familyIndex` (0-based), en bouclant sur la liste. */
// Ordre d'arrivée des kamis au fil des niveaux : celui de la liste, sauf Ujin qui n'arrive qu'au dernier niveau
const LAST_KAMI_SPRITE = '/kami/ujin.png';
const indices = FAMILY_MEMBERS.map((_, index) => index);
export const UNLOCK_ORDER = Object.freeze([
  ...indices.filter((i) => FAMILY_MEMBERS[i].sprite !== LAST_KAMI_SPRITE),
  ...indices.filter((i) => FAMILY_MEMBERS[i].sprite === LAST_KAMI_SPRITE),
]);

export function familyMember(familyIndex) {
  const index = Math.max(0, Math.floor(Number(familyIndex) || 0));
  return FAMILY_MEMBERS[index % FAMILY_MEMBERS.length];
}

/**
 * Rôle des exemplaires supplémentaires d'un même kami (niveau N : N exemplaires de chaque) : le kami d'origine est l'enfant,
 * les suivants sont son père, sa mère, son fils, sa fille, son grand-père, sa grand-mère.
 */
export const FAMILY_ROLES = Object.freeze({
  fr: ['père', 'mère', 'fils', 'fille', 'grand-père', 'grand-mère'],
  en: ['father', 'mother', 'son', 'daughter', 'grandfather', 'grandmother'],
});

/** Nom du membre dans la langue demandée (anglais en repli) ; les exemplaires suivants portent un rôle (« Izami père »). */
export function familyName(lang, familyIndex) {
  const index = Math.max(0, Math.floor(Number(familyIndex) || 0));
  const member = familyMember(index);
  const round = Math.floor(index / FAMILY_MEMBERS.length);
  const base = (member[lang] ?? member.en).name;
  if (round === 0) return base;
  const roles = FAMILY_ROLES[lang] ?? FAMILY_ROLES.en;
  return `${base} ${roles[(round - 1) % roles.length]}`;
}

/** Trait de caractère du membre dans la langue demandée (anglais en repli). */
export function familyTrait(lang, familyIndex) {
  const member = familyMember(familyIndex);
  return (member[lang] ?? member.en).trait;
}

/** Chemin du portrait (PNG détouré, fond transparent) du membre. */
export function familySprite(familyIndex) {
  return familyMember(familyIndex).sprite;
}

/**
 * Petit paragraphe sur le caractère du membre (carte de partage), dans la langue demandée (anglais en repli). Sans paragraphe pour ce
 * kami, la phrase courte (`trait`) en tient lieu.
 */
export function familyStory(lang, familyIndex) {
  const stem = /\/kami\/([^/.]+)\.png$/.exec(familySprite(familyIndex))?.[1];
  const story = KAMI_STORIES[stem];
  return story?.[lang] ?? story?.en ?? familyTrait(lang, familyIndex);
}
